import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { createDateField, ITaskTypeCol, modifyDateField, taskTypeColJsonSerializerReplacer } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { AppChartType, TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { AppReportTaskTypeTaskReference } from "ui/Scripts/source/models/appReportTemplate/appReportTaskTypeTaskReference";
import { ITaskTypeChart } from "ui/Scripts/source/models/task/taskTypeChart";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { getOpenSelectGroupingColumnDialog } from "../../TaskTypeDialogChartDesigner/ChartDesignerChartProperties/ChartDesignerChartProperties";
import { ChartGroupingFieldDialog, GroupingFieldDialogItem } from "../../TaskTypeDialogChartDesigner/ChartDesignerChartProperties/ChartGroupingFieldDialog";
import { ChartValueFieldsDialog } from "../../TaskTypeDialogChartDesigner/ChartDesignerChartProperties/ChartValueFieldsDialog";
import { ValueFieldDialogItem } from "../../TaskTypeDialogChartDesigner/ChartDesignerChartProperties/ChartValueFieldDialog";
import { TaskStateIdFilterAll } from "source/components/task/TaskPage/TaskPageUtils";

interface IFormDesignerComponentPropertiesTaskRefProps {
    companyId: string;
    formFieldMargin: string;
    taskTypeCol?: ITaskTypeCol;
    formDataCol: ITaskDataRowCol;
    taskTypes: ITaskEditTypeItem[];
    taskUnits: IIdTitleNode[];
    taskStates: IIdColorTitle[];
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
}

interface IFormDesignerComponentPropertiesTaskRefState {
    taskType: ITaskEditTypeItem;
    dateFields: IIdTitle[];
    taskChartTypes: IIdTitle[];
    selectedUnitIds: string[];
    selectedStateIds: string[];
}

export const FormDesignerComponentPropertiesTaskRef = (
    props: IFormDesignerComponentPropertiesTaskRefProps
) => {
    const objectValue = new AppReportTaskTypeTaskReference(props.taskTypeCol.objectValue);

    const getTaskTypeDateFields = (taskType: ITaskEditTypeItem): IIdTitle[] => {
        if (!taskType) return [];
        const result = taskType.formCols.filter(c => c.dataType === TaskDataRowDataType.Date).map(c => {
            return IdTitle.createIdTitle(c.field, c.title);
        });
        result.unshift(IdTitle.createIdTitle(modifyDateField, Translations.FormModifyDate));
        result.unshift(IdTitle.createIdTitle(createDateField, Translations.FormCreateDate));
        return result;
    };

    const getTaskTypeChartTypes = (): IIdTitle[] => {
        return [
            IdTitle.createIdTitle(AppChartType.BarChart.toString(10), Translations.BarChart),
            IdTitle.createIdTitle(AppChartType.LineChart.toString(10), Translations.LineChart),
            IdTitle.createIdTitle(AppChartType.PieChart.toString(10), Translations.PieChart),
            IdTitle.createIdTitle(AppChartType.Table.toString(10), Translations.Table)
        ];
    };

    const getFilterStateFromTaskGeneralFilter = (generalFilter: any) => {
        const unitIds: string[] = generalFilter?.unitIds?.filter(i => Base.isValidGuid(i)) ?? [];
        const stateIds: string[] = generalFilter?.stateIds?.filter(i => Base.isValidGuid(i)) ?? [];
        return {
            selectedUnitIds: unitIds.length > 0
                ? unitIds
                : [Base.emptyGuid],
            selectedStateIds: stateIds.length > 0
                ? stateIds
                : [TaskStateIdFilterAll],
        };
    };

    const getTaskGeneralFilterFromFilterState = (state: IFormDesignerComponentPropertiesTaskRefState) => {
        return {
            unitIds: state.selectedUnitIds
                ? state.selectedUnitIds.filter(i => i !== Base.emptyGuid)
                : null,
            stateIds: state.selectedStateIds
                ? state.selectedStateIds.filter(i => i !== TaskStateIdFilterAll)
                : null,
        };
    };

    const [state, setState] = useState<IFormDesignerComponentPropertiesTaskRefState>(() => {
        const taskType = props.taskTypes.length > 0
            ? (objectValue.taskTypeId
                ? props.taskTypes.find(t => t.id === objectValue.taskTypeId) ?? props.taskTypes[0]
                : props.taskTypes[0])
            : null;
        return {
            taskType,
            dateFields: getTaskTypeDateFields(taskType),
            taskChartTypes: getTaskTypeChartTypes(),
            ...getFilterStateFromTaskGeneralFilter(props.taskTypeCol.objectValue?.generalFilter)
        };
    });

    const [selectGroupingColumnDialogData, setSelectGroupingColumnDialogData] = useState<{ show: boolean, item: GroupingFieldDialogItem, availableFields: IIdTitle[] }>({
        show: false,
        item: null,
        availableFields: []
    });

    const [showSelectValueColumnsDialog, setShowSelectValueColumnsDialog] = useState(false);

    useEffect(() => {
        if (!props.taskTypeCol.objectValue?.generalFilter) return;
        setState((old) => ({
            ...old,
            ...getFilterStateFromTaskGeneralFilter(props.taskTypeCol.objectValue?.generalFilter)
        }));
    }, [props.taskTypeCol.objectValue?.generalFilter]);

    const handleObjectValueChange = (obj) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "objectValue",
            value: obj
        }]);
    };

    const getName = (taskTypeId: string, chart: ITaskTypeChart): string => {
        const taskType = props.taskTypes.find(r => r.id === taskTypeId);
        return `${taskType?.code || ""}, ${chart.taskChartType === AppChartType.BarChart
            ? Translations.BarChart
            : chart.taskChartType === AppChartType.LineChart
                ? Translations.LineChart
                : chart.taskChartType === AppChartType.PieChart
                    ? Translations.PieChart
                    : Translations.Table
        }`;
    };

    const getGeneralFilter = (newState: IFormDesignerComponentPropertiesTaskRefState): any => {
        const generalFilter = getTaskGeneralFilterFromFilterState(newState);
        const json = JSON.stringify(generalFilter,
            taskTypeColJsonSerializerReplacer,
            "\t"
        );
        return JSON.parse(json);
    };

    const handleOpenSelectGroupingColumnDialog = () => {
        setSelectGroupingColumnDialogData(getOpenSelectGroupingColumnDialog(state.taskType, objectValue.taskTypeChart));
    };

    //const report = props.eventReports.find(r => r.id === objectValue.reportId);

    const startDateStr = props.formDataCol?.valueObj?.startDate
        ? Base.utcTimeToDateStr(props.formDataCol?.valueObj?.startDate)
        : "";

    const endDateStr = props.formDataCol?.valueObj?.endDate
        ? Base.utcTimeToDateStr(props.formDataCol?.valueObj?.endDate)
        : "";

    return (
        <>
            {!!props.formDataCol?.valueObj?.startDate && !!props.formDataCol?.valueObj?.endDate &&
                <Grid item sm={12}>
                    <FormField title={Translations.Interval} margin={props.formFieldMargin}>
                        {`${startDateStr} - ${endDateStr}`}
                    </FormField>
                </Grid>
            }
            <Grid item sm={12}>
                <FormField title={Translations.TaskType} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={objectValue.taskTypeId}
                        items={(props.taskTypes ?? []).map(i => {
                            return { id: i.id, title: i.code };
                        })}
                        onChange={(taskTypeId) => {
                            const taskType = props.taskTypes.find(t => t.id === taskTypeId);
                            if (!taskType) return;
                            const dateFields = getTaskTypeDateFields(taskType);
                            objectValue.taskTypeId = taskTypeId;
                            objectValue.name = getName(taskTypeId, objectValue.taskTypeChart);
                            objectValue.dateField = dateFields.find(df => df.id === objectValue.dateField)
                                ? objectValue.dateField
                                : (dateFields.length > 0 ? dateFields[0].id : "");
                            handleObjectValueChange(objectValue);
                            setState((old) => ({
                                ...old,
                                taskType,
                                dateFields
                            }));
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.DateField} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={objectValue.dateField}
                        items={state.dateFields}
                        onChange={(dateField) => {
                            objectValue.dateField = dateField;
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.ChartOrTableType} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={objectValue.taskTypeChart.taskChartType.toString(10)}
                        items={state.taskChartTypes}
                        onChange={(taskChartTypeStr) => {
                            const taskChartType: AppChartType = parseInt(taskChartTypeStr, 10);
                            objectValue.taskTypeChart.taskChartType = taskChartType;
                            objectValue.name = getName(objectValue.taskTypeId, objectValue.taskTypeChart);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.ChartGroupingField} margin={props.formFieldMargin}>
                    <AppButton variant="outlined" fullWidth onClick={handleOpenSelectGroupingColumnDialog}>
                        {Translations.Edit}
                    </AppButton>
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.ChartValueFields} margin={props.formFieldMargin}>
                    <AppButton variant="outlined" fullWidth onClick={() => setShowSelectValueColumnsDialog(true)}>
                        {Translations.Edit}
                    </AppButton>
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.Unit} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        selectedIds={state.selectedUnitIds}
                        values={props.taskUnits || []}
                        onChange={(selectedUnitIds: string[]) => {
                            const newState = { ...state, selectedUnitIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.State} margin={props.formFieldMargin}>
                    <CheckboxDropdown
                        required={true}
                        selectedIds={state.selectedStateIds}
                        toggleAll={true}
                        toggleAllId={TaskStateIdFilterAll}
                        values={props.taskStates || []}
                        onChange={(selectedStateIds: string[]) => {
                            const newState = { ...state, selectedStateIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            {selectGroupingColumnDialogData.show &&
                <ChartGroupingFieldDialog
                    taskType={state.taskType}
                    selectedTaskTypeChart={objectValue.taskTypeChart}
                    item={selectGroupingColumnDialogData.item}
                    availableFields={selectGroupingColumnDialogData.availableFields}
                    onSave={(item: GroupingFieldDialogItem) => {
                        objectValue.taskTypeChart.groupingFields = [{
                            field: item.field,
                            groupingType: item.groupingType,
                            sortOrderIsDesc: item.sortOrderIsDesc,
                            dynamicValueFieldType: item.dynamicValueFieldType
                        }];
                        handleObjectValueChange(objectValue);
                        setSelectGroupingColumnDialogData((old) => ({ ...old, show: false }));
                    }}
                    onClose={() => setSelectGroupingColumnDialogData((old) => ({ ...old, show: false }))}
                />
            }
            {showSelectValueColumnsDialog &&
                <ChartValueFieldsDialog
                    taskType={state.taskType}
                    selectedTaskTypeChart={objectValue.taskTypeChart}
                    onSave={(items: ValueFieldDialogItem[]) => {
                        objectValue.taskTypeChart.valueFields = items.map(i => ({
                            field: i.field,
                            calculationType: i.calculationType,
                            title: i.title,
                            showDataLabels: i.showDataLabels
                        }));
                        handleObjectValueChange(objectValue);
                        setShowSelectValueColumnsDialog(false);
                    }}
                    onClose={() => setShowSelectValueColumnsDialog(false)}
                />
            }
        </>
    );
};
